import React, { useState, useCallback } from "react";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { MdDelete } from "react-icons/md";
import { useAppDispatch } from "../../app/hooks";
import AppDialog from "../../components/dialog/Dialog";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { PAXCOUNTINNUMBER } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";
import { closeDialog } from "../../components/dialog/dialogSlice";

interface RoomType {
  Adults: number;
  Child: number;
  Infant: number;
}

const TravellerFITBookingRoomArray = (
  fieldArrayRenderProps: FieldArrayRenderProps
) => {
  const [deleteIndex, setDeleteIndex] = useState(-1);
  const dispatch = useAppDispatch();

  const handleOpenDeleteDialog = (index: number) => {
    setDeleteIndex(index);
  };

  const handleDeleteAction = React.useCallback(
    (index: number) => {
      fieldArrayRenderProps.onRemove({ index: index });
      setDeleteIndex(-1);
    },
    [fieldArrayRenderProps]
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps.onPush({
      value: {
        Adults: 1,
        Child: 0,
        Infant: 0,
      },
    });
  }, [fieldArrayRenderProps]);

  // Get filtered options for each field based on current room values
  const getAvailableOptions = useCallback((room: RoomType, fieldName: string) => {
    const adults = parseInt(String(room?.Adults)) || 0;
    const child = parseInt(String(room?.Child)) || 0;
    const infant = parseInt(String(room?.Infant)) || 0;
    
    if (fieldName === 'Adults') {
      // Adults: 1 to 4, but limited by current children + infants
      const maxAdults = 4 - child - infant;
      return (PAXCOUNTINNUMBER || [])
        .filter((count: any) => {
          const value = parseInt(count.value);
          return value >= 1 && value <= Math.min(4, maxAdults);
        })
        .map((count: any) => ({
          value: count.value,
          label: count.label,
        }));
    } 
    else if (fieldName === 'Child') {
      // Child: 0 to 4 minus current adults and infants
      const maxChildren = 4 - adults - infant;
      const childOptions = (PAXCOUNTINNUMBER || [])
        .filter((count: any) => {
          const value = parseInt(count.value);
          return value <= Math.min(4, maxChildren);
        })
        .map((count: any) => ({
          value: count.value,
          label: count.label,
        }));
      return [{ value: 0, label: "0" }, ...childOptions];
    }
    else if (fieldName === 'Infant') {
      // Infant: 0 to 4 minus current adults and children
      const maxInfants = 4 - adults - child;
      const infantOptions = (PAXCOUNTINNUMBER || [])
        .filter((count: any) => {
          const value = parseInt(count.value);
          return value <= Math.min(4, maxInfants);
        })
        .map((count: any) => ({
          value: count.value,
          label: count.label,
        }));
      return [{ value: 0, label: "0" }, ...infantOptions];
    }
    
    return [{ value: 0, label: "0" }];
  }, []);

  // Get current rooms array safely
  const rooms = fieldArrayRenderProps.value || [];

  return (
    <GridLayout cols={[{ width: "100%" }]}>
      <GridLayoutItem
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography.h5>{"Travellers"}</Typography.h5>
        <Button
          fillMode={"solid"}
          themeColor={"primary"}
          onClick={pushElementInarray}
          type="button"
        >
          Add More Room
        </Button>
      </GridLayoutItem>

      {rooms.length === 0 && pushElementInarray()}

      {deleteIndex > -1 && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Room"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <Button
                  fillMode={"outline"}
                  themeColor={"error"}
                  onClick={() => setDeleteIndex(-1)}
                  type="button"
                  className="me-2"
                >
                  Cancel
                </Button>
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(deleteIndex)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
      <GridLayoutItem className="bg-dark text-white fw-600">
        <GridLayout
          style={{ marginRight: 40, padding: 5 }}
          gap={{ rows: 10, cols: 10 }}
          cols={[
            { width: "15%" },
            { width: "25%" },
            { width: "25%" },
            { width: "25%" },
            { width: "10%" },
          ]}
        >
          <GridLayoutItem>Room</GridLayoutItem>
          <GridLayoutItem>Adult</GridLayoutItem>
          <GridLayoutItem>Child</GridLayoutItem>
          <GridLayoutItem>Infant</GridLayoutItem>
          <GridLayoutItem></GridLayoutItem>
        </GridLayout>
      </GridLayoutItem>

      {rooms.length > 0 &&
        rooms.map((room: RoomType, index: number) => (
          <GridLayoutItem key={index} style={{ marginTop: 10 }}>
            <GridLayout
              style={{ marginRight: 40, padding: 5 }}
              gap={{ rows: 10, cols: 10 }}
              cols={[
                { width: "15%" },
                { width: "25%" },
                { width: "25%" },
                { width: "25%" },
                { width: "10%" },
              ]}
            >
              <GridLayoutItem style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    top: 5,
                    left: 10,
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </span>
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  id={`Adults-${index}`}
                  name={`HotelRooms.${index}.Adults`}
                  placeholder="Adult"
                  validator={requiredValidator}
                  component={FormSelectionField}
                  options={getAvailableOptions(room, 'Adults')}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  id={`Child-${index}`}
                  name={`HotelRooms.${index}.Child`}
                  placeholder="Child"
                  component={FormSelectionField}
                  options={getAvailableOptions(room, 'Child')}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  id={`Infant-${index}`}
                  name={`HotelRooms.${index}.Infant`}
                  placeholder="Infant"
                  component={FormSelectionField}
                  options={getAvailableOptions(room, 'Infant')}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ textAlign: "center" }}>
                <IconButton
                  onClick={() => handleOpenDeleteDialog(index)}
                  disabled={rooms.length === 1}
                  themeColor={"error"}
                  size={"small"}
                  fillMode={"solid"}
                  type="button"
                  style={{
                    height: 30,
                    width: 30,
                  }}
                  icon={MdDelete}
                  iconClassName="absolute-position"
                  iconStyle={{ fontSize: "24px" }}
                />
              </GridLayoutItem>
            </GridLayout>
          </GridLayoutItem>
        ))}

      <GridLayoutItem
        style={{
          textAlign: "end",
          marginTop: 20,
        }}
      >
        <Button
          fillMode={"solid"}
          themeColor={"primary"}
          type="button"
          onClick={() => {
            const rooms = fieldArrayRenderProps.value || [];

            const totalAdult = rooms.reduce(
              (sum: number, room: RoomType) => sum + (room.Adults || 0),
              0
            );

            const totalChild = rooms.reduce(
              (sum: number, room: RoomType) => sum + (room.Child || 0),
              0
            );
            const totalInfant = rooms.reduce(
              (sum: number, room: RoomType) => sum + (room.Infant || 0),
              0
            );
            const totalRoom = rooms.length;
            const totalTravellers = totalAdult + totalChild + totalInfant;

            const totalExtraAdult = rooms.reduce((sum: number, room: RoomType) => {
              const extra = (room.Adults || 0) - 2;
              return sum + (extra > 0 ? extra : 0);
            }, 0);

            fieldArrayRenderProps.formRenderProps.onChange("Travelers", {
              value: totalTravellers,
            });

            fieldArrayRenderProps.formRenderProps.onChange("TotalAdults", {
              value: totalAdult - totalExtraAdult,
            });

            fieldArrayRenderProps.formRenderProps.onChange("TotalCWB", {
              value: totalChild,
            });

            fieldArrayRenderProps.formRenderProps.onChange("TotalCNB", {
              value: totalInfant,
            });

            fieldArrayRenderProps.formRenderProps.onChange("TotalNoOfRooms", {
              value: totalRoom,
            });

            fieldArrayRenderProps.formRenderProps.onChange("ExtraAdult", {
              value: totalExtraAdult,
            });

            dispatch(closeDialog());
          }}
        >
          Save
        </Button>
      </GridLayoutItem>
    </GridLayout>
  );
};

export default TravellerFITBookingRoomArray;